import { useEffect, useState, useCallback } from "react";
import { Hub } from "aws-amplify";
import { useLazyQuery } from "@apollo/client";
import { USER_PUBLISHER_ROLE } from "../graphql/queries";
import { makeCookieString, deleteCookieString } from "../utils/cookie";

export const useLogin = (Auth) => {
  const [isLoggedin, setIsLoggedin] = useState(false);
  const [user, setUser] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    userPicture: null,
  });
  const [publisher, setPublisher] = useState({});
  const [userRole, setUserRole] = useState({});
  const [loading, setLoading] = useState({});

  const [getUserPublisherAndRole] = useLazyQuery(USER_PUBLISHER_ROLE, {
    onCompleted: (data) => {
      setUser({ ...data?.edShopifyUserAndPublisher?.user, userPicture: null });
      setPublisher({...data?.edShopifyUserAndPublisher?.publisher, isPro: data?.edShopifyUserAndPublisher?.publisher?.accountType !== "Free"});
      setUserRole({...data?.edShopifyUserAndPublisher?.role})
      setIsLoggedin(true);
      setLoading(false)
    },
  });

  const updatePublisherState = (value) => {
    setPublisher(value)
  }

  // Function to fetch and set user details
  const getAWSUser = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const { given_name: firstName, family_name: lastName, email } =
        user.attributes;

        setUser({
        firstName,
        lastName,
        email,
        userPicture: null, // Adjust if user profile picture is available
      });
      getUserPublisherAndRole({
        variables: {
          where: {
            ...(user.attributes.email
              ? { email: user.attributes.email }
              : { phone: user.attributes.phone_number }),
          },
        },
      });
      const currentSession = await Auth.currentSession();
      user.refreshSession(currentSession.refreshToken, (err, session) => {
        if (err) {
          console.error("Error refreshing session:", err);
          return;
        }
        const { idToken } = session;
        document.cookie = makeCookieString(
          "dyn_session",
          idToken.jwtToken,
          idToken.payload.exp,
          true
        );
      });
      // setIsLoggedin(true);
    } catch (err) {
      console.error("Error fetching user:", err);
      setIsLoggedin(false);
    }
  }, [Auth, getUserPublisherAndRole]);

  // Function to handle access token logic
  const getAccessJwtToken = useCallback(async () => {
    try {
      setLoading(true)
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const exp = session.getIdToken().payload.exp;

      if (token) {
        document.cookie = makeCookieString("dyn_session", token, exp, true);
        await getAWSUser();
      }
    } catch (err) {
      setLoading(false)
      console.error("Error getting access JWT token:", err);
    }
  }, [Auth, getAWSUser]);

  useEffect(() => {
    // Set up Hub listener
    const listener = Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signIn":
          document.cookie = makeCookieString(
            "dyn_session",
            data.signInUserSession.idToken.jwtToken,
            6
          );
          setLoading(true)
          getAWSUser();
          break;
        case "signOut":
          setIsLoggedin(false);
          document.cookie = deleteCookieString("dyn_session");
          localStorage.clear();
          sessionStorage.clear();
          break;
        default:
          break;
      }
    });

    // Cleanup listener on unmount
    return () => {
      Hub.remove("auth", listener);
    };
  }, [getAWSUser]);

  useEffect(() => {
    if (!isLoggedin) {
      getAccessJwtToken();
    }
  }, [isLoggedin, getAccessJwtToken]);

  return [isLoggedin, user, publisher, userRole, loading, updatePublisherState];
};
